import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const tagVariants = cva(
  'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0 h-6 text-xs font-semibold tracking-[0.01em] tabular-nums whitespace-nowrap',
  {
    variants: {
      tone: {
        brand: 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300',
        success: 'bg-success-bg text-success',
        info: 'bg-info-bg text-info',
        warning: 'bg-warning-bg text-warning',
        danger: 'bg-danger-bg text-destructive',
        neutral: 'bg-surface-2 text-muted-foreground',
        qty: 'bg-slate-900 text-white dark:bg-slate-700',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

const dotColor: Record<string, string> = {
  brand: 'bg-brand-600',
  success: 'bg-success',
  info: 'bg-info',
  warning: 'bg-warning',
  danger: 'bg-destructive',
  neutral: 'bg-muted-foreground',
  qty: 'bg-white',
};

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof tagVariants> {
  /** Renders a small leading dot in the tone color, so status isn't communicated by color alone. */
  dot?: boolean;
}

export function Tag({ className, tone = 'neutral', dot = false, children, ...props }: TagProps) {
  return (
    <span className={cn(tagVariants({ tone }), className)} {...props}>
      {dot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotColor[tone ?? 'neutral'])} aria-hidden="true" />}
      {children}
    </span>
  );
}
